import {
  Component,
  ElementRef,
  Renderer2,
  ViewEncapsulation,
  inject,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { faTag, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faEdit } from '@fortawesome/pro-solid-svg-icons';
import { MultiSelectComponent, RemoveTagEvent } from '@progress/kendo-angular-dropdowns';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { UserOperation } from '../../../core/enums/user-operation';
import { TagModel } from '../../../core/models/tag/tag.model';
import { ShiveDialogService } from '../../../core/services/controls/shive-dialog.service';
import { TagsHttpService } from '../../../core/state/tags/tags-http.service';
import { generateDummyId } from '../../functions/array-functions';
import { ShiveTaglistDialogComponent } from './shive-taglist-dialog/shive-taglist-dialog.component';

@Component({
  selector: 'app-shive-taglist',
  standalone: false,
  templateUrl: './shive-taglist.component.html',
  styleUrl: './shive-taglist.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ShiveTaglistComponent {
  // Signals and inputs
  selectedTags = model.required<TagModel[]>();
  unselectedTags = signal<TagModel[]>([]);
  loading = signal<boolean>(false);
  multiSelectCmp = viewChild<MultiSelectComponent>('multiSelectComponent');

  // Misc
  public readonly faTag = faTag;
  public readonly faEdit = faEdit;
  public readonly faTrash = faTrash;

  // DI
  private readonly tagsHttpService = inject(TagsHttpService);
  private readonly shiveDialogService = inject(ShiveDialogService);
  private readonly renderer = inject(Renderer2);
  private readonly elementRef = inject(ElementRef);

  // Constants
  private allTags: TagModel[] = [];

  public valueNormalizer = (text$: Observable<string>) =>
    text$.pipe(
      switchMap((text: string) => {
        // search for matching item to avoid duplicates
        // search in values
        const selected = this.multiSelectCmp().tags as TagModel[];
        const matchingValue: TagModel = selected.find((item: TagModel) => {
          return item.name.toLowerCase() === text.toLowerCase();
        });

        if (matchingValue) {
          return of(null); // return the already selected matching value and the component will remove it
        }

        const newTag: TagModel = {
          id: generateDummyId(this.allTags), // generate unique value for the custom item
          name: text,
          color: '#A0A0A0',
        };

        return this.tagsHttpService.createTag(newTag);
      }),
      map((newTagResponse: TagModel) => {
        if (!newTagResponse) {
          return null;
        }

        const newTag = new TagModel();
        newTag.id = newTagResponse.id;
        newTag.name = newTagResponse.name;
        newTag.color = newTagResponse.color;
        return newTag;
      }),
    );

  public change(selectedTags: TagModel[]): void {
    this.selectedTags.set(selectedTags);
    this.unselectedTags.update((unselected) => {
      return unselected.filter((t) => !selectedTags.some((f) => f.name === t.name));
    });
    this.multiSelectCmp().text = '';

    window.setTimeout(() => this.applyChipColors());
  }

  public removeTag($event: RemoveTagEvent): void {
    const item = $event.dataItem;

    // Add tag to the dropdown again.
    this.unselectedTags.update((old) => {
      // Prevent duplicates
      if (old.some((t) => t.name === item.value)) {
        return old;
      }
      const current = [...old, item];
      return current.sort((a, b) => a.id - b.id);
    });
  }

  public opened(): void {
    this.loading.set(true);
    this.tagsHttpService
      .getTags()
      .pipe(take(1))
      .subscribe((tags) => {
        this.allTags = [...tags];
        this.loading.set(false);
        const selected = this.multiSelectCmp().tags as TagModel[];
        const unselected = tags
          .filter((t) => !selected.some((f) => f.name === t.name))
          .sort((a, b) => a.id - b.id);

        this.unselectedTags.set(unselected);
      });
  }

  public closed(): void {
    this.allTags = [];
    this.unselectedTags.set([]);
  }

  public deleteTag(tag: TagModel, $event: PointerEvent): void {
    $event.stopPropagation();
    this.unselectedTags.update((old) => old.filter((t) => t.name !== tag.name));

    this.tagsHttpService
      .deleteTag(tag)
      .pipe(take(1))
      .subscribe(() => {
        this.opened();
      });
  }

  public handleFilter(searchTerm: string): void {
    const contains = (value: string) => (item: TagModel) =>
      item.name.toLowerCase().includes(value.toLowerCase());

    this.unselectedTags.set(this.allTags.filter(contains(searchTerm)));
  }

  public openDialog(tag: TagModel): void {
    const ref = this.shiveDialogService.open({
      content: ShiveTaglistDialogComponent,
      width: 572,
      title: 'Tag bearbeiten',
    });
    ref.content.setInput('tag', tag);

    ref.result
      .pipe(
        filter((value) => value !== UserOperation.Cancel),
        take(1),
      )
      .subscribe((result: TagModel) => {
        this.tagsHttpService.updateTag(result);
      });
  }

  private applyChipColors(): void {
    const chips = this.elementRef.nativeElement.querySelectorAll('.k-chip');
    const tags = this.selectedTags();

    chips.forEach((chip: HTMLElement, index: number) => {
      if (tags[index]?.color) {
        this.renderer.setStyle(chip, 'background-color', tags[index].color);
        this.renderer.setStyle(chip, 'border-color', tags[index].color);
      }
    });
  }
}
